import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'
import type {
  AttendanceEvent,
  AttendanceEventFilters,
  PaginatedAttendanceEvents,
  CreateAttendanceEventDto,
} from '../types/attendance.types'

const ATTENDANCE_QUERY_KEY = 'attendance-events'

export const useAttendanceEvents = (filters: AttendanceEventFilters = {}) => {
  return useQuery({
    queryKey: [ATTENDANCE_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedAttendanceEvents> => {
      const response = await apiClient.getAttendanceEvents(filters) as { data: AttendanceEvent[]; meta?: { total: number; page: number; limit: number; totalPages: number } }
      // API returns { data: [...], meta: { total, page, limit, totalPages } }
      const meta = response.meta || { total: 0, page: 1, limit: 20, totalPages: 1 }
      return {
        data: response.data || [],
        total: meta.total,
        page: meta.page,
        limit: meta.limit,
        totalPages: meta.totalPages,
      }
    },
    staleTime: 30 * 1000, // 30 seconds
    retry: 3,
  })
}

export const useAttendanceEvent = (id: string) => {
  return useQuery({
    queryKey: [ATTENDANCE_QUERY_KEY, id],
    queryFn: async (): Promise<AttendanceEvent> => {
      const response = await apiClient.get(`/api/v1/attendance-events/${id}`)
      return response.data
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  })
}

export const useCreateAttendanceEvent = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateAttendanceEventDto) => {
      const response = await apiClient.createAttendanceEvent(data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ATTENDANCE_QUERY_KEY] })
    },
  })
}

export const useAttendanceStatistics = (filters: AttendanceEventFilters = {}) => {
  return useQuery({
    queryKey: [ATTENDANCE_QUERY_KEY, 'statistics', filters],
    queryFn: async () => {
      const response = await apiClient.get('/api/v1/attendance-events/statistics', {
        params: filters,
      })
      return response.data
    },
    staleTime: 60 * 1000, // 1 minute
  })
}

export const useAttendanceEventsByEmployee = (employeeId: string, filters: AttendanceEventFilters = {}) => {
  return useQuery({
    queryKey: [ATTENDANCE_QUERY_KEY, 'employee', employeeId, filters],
    queryFn: async (): Promise<PaginatedAttendanceEvents> => {
      const response = await apiClient.get('/api/v1/attendance-events/by-employee', {
        params: { employeeId, ...filters },
      })
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      }
    },
    enabled: !!employeeId,
    staleTime: 30 * 1000,
  })
}